// New York: the MTA's systems, PATH and NJ Transit as the GTFS pipeline
// (@tol/gtfs) sees them, their lines, and how their GTFS ids become the
// page's station and line ids. Shared by the network build
// (src/server/build.ts), the server's live feed and the page. NJ Transit's
// systems read tables built from its GTFS, so they're the server's
// (src/server/njt.ts).

import type { GtfsSystem, LineAlert } from '@tol/gtfs/types';
import { RAILROAD_REACH } from '@tol/gtfs/network-file';
import busesJson from './buses.json' with { type: 'json' };

export type NycSystem = 'subway' | 'lirr' | 'mnr' | 'path' | 'njt' | 'njlr' | 'bus';

// https://api.mta.info/ lists these. No key needed.
const FEED_BASE = 'https://api-endpoint.mta.info/Dataservice/mtagtfsfeeds/';
const feed = (id: string, path: string): { readonly id: string; readonly url: string } => ({
  id,
  url: FEED_BASE + path,
});

// PATH's stations by the codes RidePATH names them with (path.ts), from
// its platforms' stop ids in the static GTFS (two a station, four at Journal
// Square; the same ids since at least the Port Authority's 2025 feed) and
// the timetable's "place_" ids.
const PATH_PLATFORMS: Readonly<Record<string, string>> = Object.fromEntries(
  (
    [
      ['NWK', '781718 781719'],
      ['HAR', '781720 781721'],
      ['JSQ', '781722 781723 781724 781725'],
      ['GRV', '781726 781727'],
      ['NEW', '781728 781729'],
      ['EXP', '781730 781731'],
      ['CHR', '781732 781733'],
      ['09S', '781734 781735'],
      ['14S', '781736 781737'],
      ['23S', '781738 781739'],
      ['33S', '781740 781742'],
      ['HOB', '781743 781744'],
      ['WTC', '781747 781750'],
    ] as const
  ).flatMap(([code, ids]) => ids.split(' ').map((id) => [id, code] as const)),
);

// A subway platform ("142S") is its station ("142") plus a direction. The
// railroads' stop ids are bare numbers that collide with each other and with
// the subway's, so they get a prefix. A PATH platform is its station's code
// ("path-HOB").
export function stationId(system: NycSystem, stopId: string): string {
  if (system === 'subway') return /^[A-Z0-9]{3}[NS]$/u.test(stopId) ? stopId.slice(0, 3) : stopId;
  if (system === 'path') return `path-${PATH_PLATFORMS[stopId] ?? stopId.replace(/^place_/u, '')}`;
  return `${system}-${stopId}`;
}

// Express variants run as their line: the 6 <6> is the 6.
const SUBWAY_MERGE: Readonly<Record<string, string>> = { '6X': '6', '7X': '7', FX: 'F' };

export function lineId(system: NycSystem, routeId: string): string {
  if (system === 'subway') return SUBWAY_MERGE[routeId] ?? routeId;
  return `${system}-${routeId}`;
}

// A subway trip's direction from its stop ids ("142S": southbound). GTFS
// direction_id 0 is north, 1 is south.
export function subwayDirection(stopId: string): 0 | 1 | null {
  if (stopId.endsWith('N')) return 0;
  if (stopId.endsWith('S')) return 1;
  return null;
}

// A subway trip id ends in its shape id: "062900_1..S15R".
const subwayShape = (tripId: string): string => tripId.slice(tripId.indexOf('_') + 1);

export interface NycLine {
  readonly id: string;
  readonly system: NycSystem;
  // What the roster shows: "A", "Babylon".
  readonly name: string;
  // Drawn on the line's trains zoomed in: the subway's route bullet. None
  // for the railroads, whose lines have no letters.
  readonly bullet?: string;
  // Cards and hover labels: "A train", "LIRR Babylon Branch".
  readonly label: string;
  // Official color (the static GTFS route_color).
  readonly color: string;
  // On-map color, brighter where the official one vanishes on the dark map.
  readonly show: string;
  // Drawn hollow, like London's Overground: the commuter railroads.
  readonly hollow: boolean;
  // A bus: the minor tier (PageLine.minor, @tol/core/minor).
  readonly minor?: true;
}

type LineRow = readonly [id: string, system: NycSystem, name: string, label: string, color: string, show?: string];

// The commuter railroads, drawn hollow; the subway, PATH and light rail solid.
const HOLLOW: ReadonlySet<NycSystem> = new Set(['lirr', 'mnr', 'njt']);

// Colors from each system's routes.txt (September 2026): the MTA's, PATH's
// (the Port Authority's, via National RTAP, August 10, 2026) and NJ
// Transit's (rail_data.zip, September 16, 2026).
const LINE_ROWS: readonly LineRow[] = [
  ['1', 'subway', '1', '1 train', '#D82233'],
  ['2', 'subway', '2', '2 train', '#D82233'],
  ['3', 'subway', '3', '3 train', '#D82233'],
  ['4', 'subway', '4', '4 train', '#009952'],
  ['5', 'subway', '5', '5 train', '#009952'],
  ['6', 'subway', '6', '6 train', '#009952'],
  ['7', 'subway', '7', '7 train', '#9A38A1', '#B24DB9'],
  ['A', 'subway', 'A', 'A train', '#0062CF', '#2A7FE6'],
  ['C', 'subway', 'C', 'C train', '#0062CF', '#2A7FE6'],
  ['E', 'subway', 'E', 'E train', '#0062CF', '#2A7FE6'],
  ['B', 'subway', 'B', 'B train', '#EB6800'],
  ['D', 'subway', 'D', 'D train', '#EB6800'],
  ['F', 'subway', 'F', 'F train', '#EB6800'],
  ['M', 'subway', 'M', 'M train', '#EB6800'],
  ['G', 'subway', 'G', 'G train', '#799534', '#8FB040'],
  ['J', 'subway', 'J', 'J train', '#8E5C33', '#A87445'],
  ['Z', 'subway', 'Z', 'Z train', '#8E5C33', '#A87445'],
  ['L', 'subway', 'L', 'L train', '#7C858C', '#98A1A8'],
  ['N', 'subway', 'N', 'N train', '#F6BC26'],
  ['Q', 'subway', 'Q', 'Q train', '#F6BC26'],
  ['R', 'subway', 'R', 'R train', '#F6BC26'],
  ['W', 'subway', 'W', 'W train', '#F6BC26'],
  ['GS', 'subway', 'S 42 St', '42 St Shuttle', '#7C858C', '#98A1A8'],
  ['FS', 'subway', 'S Franklin Av', 'Franklin Av Shuttle', '#7C858C', '#98A1A8'],
  ['H', 'subway', 'S Rockaway Park', 'Rockaway Park Shuttle', '#7C858C', '#98A1A8'],
  ['SI', 'subway', 'SIR', 'Staten Island Railway', '#08179C', '#4A62E0'],
  ['lirr-1', 'lirr', 'Babylon', 'LIRR Babylon Branch', '#00985F'],
  ['lirr-2', 'lirr', 'Hempstead', 'LIRR Hempstead Branch', '#CE8E00'],
  ['lirr-3', 'lirr', 'Oyster Bay', 'LIRR Oyster Bay Branch', '#00AF3F'],
  ['lirr-4', 'lirr', 'Ronkonkoma', 'LIRR Ronkonkoma Branch', '#A626AA', '#C040C4'],
  ['lirr-5', 'lirr', 'Montauk', 'LIRR Montauk Branch', '#00B2A9'],
  ['lirr-6', 'lirr', 'Long Beach', 'LIRR Long Beach Branch', '#FF6319'],
  ['lirr-7', 'lirr', 'Far Rockaway', 'LIRR Far Rockaway Branch', '#6E3219', '#A0583A'],
  ['lirr-8', 'lirr', 'West Hempstead', 'LIRR West Hempstead Branch', '#00A1DE'],
  ['lirr-9', 'lirr', 'Port Washington', 'LIRR Port Washington Branch', '#C60C30', '#E8324F'],
  ['lirr-10', 'lirr', 'Port Jefferson', 'LIRR Port Jefferson Branch', '#006EC7', '#2A8AE0'],
  ['lirr-11', 'lirr', 'Belmont Park', 'LIRR Belmont Park', '#60269E', '#8450C8'],
  ['lirr-12', 'lirr', 'City Terminal Zone', 'LIRR City Terminal Zone', '#4D5357', '#8A9096'],
  ['lirr-13', 'lirr', 'Greenport', 'LIRR Greenport Service', '#A626AA', '#C040C4'],
  ['mnr-1', 'mnr', 'Hudson', 'Metro-North Hudson Line', '#009B3A'],
  ['mnr-2', 'mnr', 'Harlem', 'Metro-North Harlem Line', '#0039A6', '#2F6BDB'],
  ['mnr-3', 'mnr', 'New Haven', 'Metro-North New Haven Line', '#EE0034'],
  ['mnr-4', 'mnr', 'New Canaan', 'Metro-North New Canaan Branch', '#EE0034'],
  ['mnr-5', 'mnr', 'Danbury', 'Metro-North Danbury Branch', '#EE0034'],
  ['mnr-6', 'mnr', 'Waterbury', 'Metro-North Waterbury Branch', '#EE0034'],
  ['path-RED', 'path', 'Newark–WTC', 'PATH Newark–World Trade Center', '#D93A30'],
  ['path-GRE', 'path', 'Hoboken–WTC', 'PATH Hoboken–World Trade Center', '#65C100'],
  ['path-BLU', 'path', 'Hoboken–33rd St', 'PATH Hoboken–33rd Street', '#4D92FB'],
  ['path-YEL', 'path', 'Journal Sq–33rd St', 'PATH Journal Square–33rd Street', '#FF9900'],
  ['path-ATW', 'path', 'Journal Sq–33rd St via Hoboken', 'PATH Journal Square–33rd Street via Hoboken', '#FF9900'],
  ['njt-NEC', 'njt', 'Northeast Corridor', 'NJ Transit Northeast Corridor Line', '#DD3439', '#EE4A4F'],
  ['njt-NJCL', 'njt', 'North Jersey Coast', 'NJ Transit North Jersey Coast Line', '#03A3DF'],
  ['njt-RARV', 'njt', 'Raritan Valley', 'NJ Transit Raritan Valley Line', '#F2A537'],
  ['njt-MNE', 'njt', 'Morris & Essex', 'NJ Transit Morris & Essex Line', '#08A652'],
  ['njt-MNEG', 'njt', 'Gladstone', 'NJ Transit Gladstone Branch', '#A4C9AA'],
  ['njt-BNTN', 'njt', 'Montclair-Boonton', 'NJ Transit Montclair-Boonton Line', '#E66859'],
  ['njt-MNBN', 'njt', 'Main/Bergen County', 'NJ Transit Main/Bergen County Line', '#FFD411'],
  ['njt-MNBNP', 'njt', 'Port Jervis', 'NJ Transit Port Jervis Line', '#FFD411'],
  ['njt-PASC', 'njt', 'Pascack Valley', 'NJ Transit Pascack Valley Line', '#94219A', '#B843BE'],
  ['njt-MRL', 'njt', 'Meadowlands', 'NJ Transit Meadowlands Rail Line', '#C1AA72'],
  ['njt-PRIN', 'njt', 'Princeton', 'NJ Transit Princeton Branch', '#DD3439', '#EE4A4F'],
  ['njt-ATLC', 'njt', 'Atlantic City', 'NJ Transit Atlantic City Rail Line', '#075AAA', '#2F80D6'],
  ['njlr-HBLR', 'njlr', 'Hudson-Bergen', 'Hudson-Bergen Light Rail', '#008080', '#1AA7A7'],
  ['njlr-NLR', 'njlr', 'Newark', 'Newark Light Rail', '#009AD1'],
  ['njlr-RVLN', 'njlr', 'River Line', 'River LINE light rail', '#00008B', '#4A64E0'],
];

// The shuttles are all "S"; the Staten Island Railway's bullet is "SIR".
const BULLET: Readonly<Record<string, string>> = { GS: 'S', FS: 'S', H: 'S', SI: 'SIR' };

// The MTA's bus routes with trips in its bus timetables, written by
// scripts/build-network.ts (src/server/buses.ts busRows): [route_id,
// route_short_name, route_long_name, route_color]. Route ids are the
// ones Bus Time's feeds and the bus alerts use ("B44+", "Q06"); the short
// name is the bullet ("B44-SBS" is "B44+", "Q6" is "Q06").
type BusRow = readonly [route: string, short: string, name: string, color: string];

const isBusRows = (v: unknown): v is readonly BusRow[] =>
  Array.isArray(v) && v.every((r) => Array.isArray(r) && r.length === 4 && r.every((x) => typeof x === 'string'));
function busRows(v: unknown): readonly BusRow[] {
  if (!isBusRows(v)) throw new TypeError('buses.json is not a list of bus routes');
  return v;
}

export const BUS_ROWS: readonly BusRow[] = busRows(busesJson);

export const NYC_LINES: readonly NycLine[] = [
  ...LINE_ROWS.map(([id, system, name, label, color, show]) => {
    const line: NycLine = { id, system, name, label, color, show: show ?? color, hollow: HOLLOW.has(system) };
    return system === 'subway' ? Object.assign(line, { bullet: BULLET[id] ?? id }) : line;
  }),
  ...BUS_ROWS.map(([route, short, name, color]): NycLine => ({
    id: lineId('bus', route),
    system: 'bus',
    name: short,
    bullet: short,
    label: name === '' ? `${short} bus` : `${short} ${name}`,
    color: `#${color}`,
    show: `#${color}`,
    hollow: false,
    minor: true,
  })),
];

export const linesOf = (system: NycSystem): ReadonlySet<string> =>
  new Set(NYC_LINES.filter((l) => l.system === system).map((l) => l.id));

// The three systems for the pipeline. Only the LIRR's GPS is trusted:
// Metro-North's is on a few trains and not always the right ones (on
// September 25, 2026 a southbound New Haven line train and a northbound
// Harlem line one reported the same fixes, and several trips kept coming
// back to one point). The train numbers: the LIRR's is the end of its trip
// id, Metro-North's the entity id.
export const MTA_SYSTEMS: readonly GtfsSystem[] = [
  {
    id: 'subway',
    static: 'https://rrgtfsfeeds.s3.amazonaws.com/gtfs_subway.zip',
    feeds: [
      feed('1234567S', 'nyct%2Fgtfs'),
      feed('ACE', 'nyct%2Fgtfs-ace'),
      feed('BDFM', 'nyct%2Fgtfs-bdfm'),
      feed('G', 'nyct%2Fgtfs-g'),
      feed('JZ', 'nyct%2Fgtfs-jz'),
      feed('NQRW', 'nyct%2Fgtfs-nqrw'),
      feed('L', 'nyct%2Fgtfs-l'),
      feed('SIR', 'nyct%2Fgtfs-si'),
    ],
    lines: linesOf('subway'),
    stationId: (stopId) => stationId('subway', stopId),
    lineId: (routeId) => lineId('subway', routeId),
    direction: (_trip, firstStop) => subwayDirection(firstStop),
    shapeOf: subwayShape,
    // Subway trips without a shape_id use the one in their trip id.
    staticShapeOf: subwayShape,
  },
  {
    id: 'lirr',
    static: 'https://rrgtfsfeeds.s3.amazonaws.com/gtfslirr.zip',
    feeds: [feed('LIRR', 'lirr%2Fgtfs-lirr')],
    lines: linesOf('lirr'),
    stationId: (stopId) => stationId('lirr', stopId),
    lineId: (routeId) => lineId('lirr', routeId),
    vehicleId: (e) => e.tripUpdate?.trip.tripId.split('_').at(-1) ?? '',
    gps: true,
    reach: RAILROAD_REACH,
  },
  {
    id: 'mnr',
    static: 'https://rrgtfsfeeds.s3.amazonaws.com/gtfsmnr.zip',
    feeds: [feed('MNR', 'mnr%2Fgtfs-mnr')],
    lines: linesOf('mnr'),
    stationId: (stopId) => stationId('mnr', stopId),
    lineId: (routeId) => lineId('mnr', routeId),
    vehicleId: (e) => e.id,
    reach: RAILROAD_REACH,
  },
];

// PATH's timetable: the Port Authority's static GTFS, published through
// National RTAP's GTFS Builder (Transitland lists it as PATH's current feed,
// f-dr5r-path~nj~us). Its live trains are the timetable's, set by RidePATH's
// arrival times (src/server/path.ts): the Port Authority publishes no
// GTFS-Realtime, so the system has no feeds of its own.
export const PATH_STATIC = 'https://rapid.nationalrtap.org/GTFSFileManagement/UserUploadFiles/14843/PATHGTFS.zip';

export const PATH_SYSTEM: GtfsSystem = {
  id: 'path',
  static: PATH_STATIC,
  feeds: [],
  lines: linesOf('path'),
  stationId: (stopId) => stationId('path', stopId),
  lineId: (routeId) => lineId('path', routeId),
};

// The buses: the MTA's six bus timetables joined (src/server/buses.ts),
// their trips from Bus Time's GTFS-Realtime (https://bustime.mta.info/wiki/Developers/Index),
// whose trip, route and stop ids are the timetables' (3,984 of 4,045 trip
// updates' trips and every route but the shuttles on September 26, 2026).
// Each trip lists the calls ahead with times; each vehicle has fresh GPS.
// Bus Time requires a registered key: without one no live feeds are polled.
// Static bus geometry remains available. Every stop is
// its own station ("bus-<stop_id>"): stop ids are bare numbers, and a bus
// stop outside a subway station is a place of its own.
export const BUSTIME = 'https://gtfsrt.prod.obanyc.com/';

// Bus Time's feeds are 2.3 MB (trips) and 0.3 MB (vehicles) and are
// regenerated every 30 s: fetched no more often than that, whatever the
// subway's pace.
export const BUS_REFRESH_MS = 30_000;

export function busSystem(key = ''): GtfsSystem {
  const query = key === '' ? '' : `?key=${encodeURIComponent(key)}`;
  return {
    id: 'bus',
    // Six zips, not one (src/server/buses.ts BUS_ZIPS): the build reads
    // them itself; this is the first, for the record.
    static: 'https://rrgtfsfeeds.s3.amazonaws.com/gtfs_b.zip',
    feeds:
      key === ''
        ? []
        : [
            {
              id: 'bus',
              url: `${BUSTIME}tripUpdates${query}`,
              vehicles: `${BUSTIME}vehiclePositions${query}`,
              refreshMs: BUS_REFRESH_MS,
            },
          ],
    lines: linesOf('bus'),
    stationId: (stopId) => stationId('bus', stopId),
    lineId: (routeId) => lineId('bus', routeId),
    gps: true,
  };
}

export const NETWORK_SOURCE =
  "Static GTFS shapes.txt: the MTA's (subway, LIRR, Metro-North, buses), PATH's and NJ Transit's (rail, light rail)";

// ---- GET /alerts (src/server/alerts.ts)

// A line's most serious active alert.
export type NycAlert = LineAlert;

export interface NycAlerts {
  // When they were fetched, ms (0: never).
  readonly time: number;
  readonly lines: Readonly<Record<string, NycAlert>>;
  // The lines the MTA's alerts speak for: every line, once they've come.
  readonly covered?: readonly string[];
}
